import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CircularProgress,
    Tab,
    Tabs,
    Typography,
    useTheme,
} from "@mui/material";
import { AutoGraphRounded } from "@mui/icons-material";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    Report_corporateCreditCountByMonth,
    Report_corporatePersonnelByMonth,
} from "../../../network/api/report.api";

const _DashMonthlyGrowth = ({ corporateId }) => {
    const theme = useTheme();
    const [tab, setTab] = useState(0); // 0: Personnel, 1: Credit Count
    const [personnelData, setPersonnelData] = useState([]);
    const [countData, setCountData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!corporateId) return;
        setLoading(true);
        Promise.all([
            Report_corporatePersonnelByMonth(corporateId).catch(() => null),
            Report_corporateCreditCountByMonth(corporateId).catch(() => null),
        ])
            .then(([resP, resC]) => {
                const pPoints = resP?.data?.Data?.Series?.[0]?.Points || [];
                const cPoints = resC?.data?.Data?.Series?.[0]?.Points || [];

                setPersonnelData(pPoints);
                setCountData(cPoints);
            })
            .finally(() => setLoading(false));
    }, [corporateId]);

    const activeConfig = [
        {
            title: "رشد تعداد پرسنل",
            data: personnelData,
            color: theme.palette.primary.main,
            unit: "نفر",
            formatter: (v) => `${v} نفر`,
        },
        {
            title: "تعداد بسته‌های اعتباری",
            data: countData,
            color: "#9c27b0",
            unit: "تعداد",
            formatter: (v) => `${v} بسته`,
        },
    ][tab] || {
        title: "رشد تعداد پرسنل",
        data: personnelData,
        color: theme.palette.primary.main,
        unit: "نفر",
        formatter: (v) => `${v} نفر`,
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                        direction: 'rtl',
                        textAlign: 'right',
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5, color: 'text.secondary' }}>
                        دوره: {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: activeConfig.color }}>
                        {activeConfig.formatter(payload[0].value)}
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    return (
        <Card
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2.5,
                            bgcolor: 'rgba(45, 107, 226, 0.1)',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <AutoGraphRounded sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            روندهای ماهانه سازمان (۱۲ ماه)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            پایش رشد منابع انسانی و جریان اعتبارات رفاهی
                        </Typography>
                    </Box>
                </Box>

                <Tabs
                    value={tab}
                    onChange={(e, val) => setTab(val)}
                    size="small"
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{
                        minHeight: 36,
                        bgcolor: 'action.hover',
                        p: 0.5,
                        borderRadius: 2.5,
                        '& .MuiTab-root': {
                            minHeight: 32,
                            py: 0.5,
                            px: 1.5,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            borderRadius: 2,
                        },
                    }}
                >
                    <Tab label="تعداد پرسنل" />
                    <Tab label="تعداد اعتبار" />
                </Tabs>
            </Box>

            <Box sx={{ width: '100%', height: 280, mt: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : activeConfig.data.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                            داده‌ای در این دوره ثبت نشده است.
                        </Typography>
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={activeConfig.data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={activeConfig.color} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={activeConfig.color} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                            <XAxis
                                dataKey="Label"
                                stroke={theme.palette.text.secondary}
                                fontSize={11}
                                tickLine={false}
                            />
                            <YAxis
                                stroke={theme.palette.text.secondary}
                                fontSize={11}
                                tickLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="Value"
                                stroke={activeConfig.color}
                                strokeWidth={2.5}
                                fillOpacity={1}
                                fill="url(#growthGrad)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </Box>
        </Card>
    );
};

export default _DashMonthlyGrowth;
